/**
 * Creative Table Component
 *
 * Antares-style creatives list with compact rows, inline thumbnails,
 * status badges, and multiple sortable metric columns.
 */

import { useState } from 'react'
import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import type { CreativeWithPerformance, CreativeStatus } from '../types'

interface CreativeTableProps {
  creatives: CreativeWithPerformance[]
  loading?: boolean
  onCreativeClick?: (creative: CreativeWithPerformance) => void
}

// ============================================================================
// Helpers
// ============================================================================

function fmtCurrency(value: number): string {
  if (value >= 100_000) return `$${(value / 1_000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` // e.g. $70,774
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtCompact(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function getStatusBadge(status: CreativeStatus) {
  const map: Record<string, { label: string; bg: string; text: string }> = {
    WINNER:         { label: 'Winner',         bg: 'bg-tf-feedback-success-bg', text: 'text-tf-feedback-success-text' },
    RISING_STAR:    { label: 'Rising Star',    bg: 'bg-tf-feedback-info-bg',    text: 'text-tf-feedback-info-text' },
    TESTING:        { label: 'Testing',        bg: 'bg-tf-feedback-warning-bg', text: 'text-tf-feedback-warning-text' },
    UNDERPERFORMER: { label: 'Underperformer', bg: 'bg-tf-feedback-error-bg',   text: 'text-tf-feedback-error-text' },
    PAUSED:         { label: 'To Be Paused',   bg: 'bg-tf-feedback-warning-bg', text: 'text-tf-feedback-warning-text' },
  }
  return map[status] || { label: status, bg: 'bg-tf-bg-muted', text: 'text-tf-text-muted' }
}

// ============================================================================
// Sort types
// ============================================================================

type SortField = 'ad_name' | 'spend' | 'revenue' | 'roas' | 'ctr' | 'cpc' | 'cpm' | 'days_running'
type SortOrder = 'asc' | 'desc'

function getSortValue(c: CreativeWithPerformance, field: SortField): number | string {
  switch (field) {
    case 'ad_name': return (c.ad_name || '').toLowerCase()
    case 'spend': return c.metrics?.spend || 0
    case 'revenue': return c.metrics?.revenue || 0
    case 'roas': return c.metrics?.roas || 0
    case 'ctr': return c.metrics?.ctr || 0
    case 'cpc': return c.metrics?.cpc || 0
    case 'cpm': return c.metrics?.cpm || 0
    case 'days_running': return c.days_running || 0
    default: return 0
  }
}

const ITEMS_PER_PAGE = 10

// ============================================================================
// Component
// ============================================================================

export function CreativeTable({ creatives, loading, onCreativeClick }: CreativeTableProps) {
  const [sortField, setSortField] = useState<SortField>('spend')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<CreativeStatus | ''>('')
  const [currentPage, setCurrentPage] = useState(1)

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  // Filter + sort
  const filtered = creatives
    .filter((c) => {
      const q = searchQuery.toLowerCase()
      const matchSearch =
        !q ||
        (c.ad_name || '').toLowerCase().includes(q) ||
        (c.campaign_name || '').toLowerCase().includes(q)
      const matchStatus = !statusFilter || c.performance_status === statusFilter
      return matchSearch && matchStatus
    })
    .sort((a, b) => {
      const av = getSortValue(a, sortField)
      const bv = getSortValue(b, sortField)
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      return sortOrder === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
  const page = filtered.slice(startIdx, startIdx + ITEMS_PER_PAGE)

  const setFilter = (search: string, status: CreativeStatus | '') => {
    setSearchQuery(search)
    setStatusFilter(status)
    setCurrentPage(1)
  }

  // Column header helper
  const TH = ({ field, label, align = 'right' }: { field: SortField; label: string; align?: 'left' | 'right' }) => (
    <th
      className={cn(
        'px-3 py-2.5 text-[11px] font-medium text-tf-text-muted uppercase tracking-wider cursor-pointer select-none hover:text-tf-text-sub transition-colors whitespace-nowrap',
        align === 'left' ? 'text-left' : 'text-right',
      )}
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field && (
        <span className="ml-0.5 inline-block">
          {sortOrder === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </th>
  )

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
        <div className="px-5 py-4 border-b border-tf-border-sub">
          <div className="h-5 w-40 bg-tf-bg-hover rounded animate-pulse" />
        </div>
        <div className="divide-y divide-tf-border-sub">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-3">
              <div className="h-10 w-10 bg-tf-bg-hover rounded animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 bg-tf-bg-hover rounded w-2/5 animate-pulse" />
                <div className="h-3 bg-tf-bg-muted rounded w-1/4 animate-pulse" />
              </div>
              <div className="h-3.5 w-16 bg-tf-bg-hover rounded animate-pulse" />
              <div className="h-3.5 w-16 bg-tf-bg-hover rounded animate-pulse" />
              <div className="h-3.5 w-12 bg-tf-bg-hover rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (creatives.length === 0) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-12 text-center">
        <Icons.Image className="w-12 h-12 text-tf-text-muted mx-auto mb-4" />
        <h4 className="text-base font-medium text-tf-text mb-2">No Creatives Found</h4>
        <p className="text-sm text-tf-text-muted">
          Connect your Meta account and sync data to view creatives
        </p>
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
      {/* Header - matches Antares "Creatives Ads" header */}
      <div className="px-5 py-3 border-b border-tf-border-sub">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-tf-text">Creatives Ads</h3>
          <div className="flex items-center gap-2">
            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setFilter(searchQuery, e.target.value as CreativeStatus | '')}
              className="h-8 px-2.5 text-xs border border-tf-border rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none bg-tf-bg-card text-tf-text-muted"
            >
              <option value="">All Status</option>
              <option value="WINNER">Winner</option>
              <option value="RISING_STAR">Rising Star</option>
              <option value="TESTING">Testing</option>
              <option value="UNDERPERFORMER">Underperformer</option>
              <option value="PAUSED">Paused</option>
            </select>
            {/* Search */}
            <div className="relative">
              <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-tf-text-muted" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setFilter(e.target.value, statusFilter)}
                className="h-8 pl-8 pr-3 text-xs border border-tf-border rounded-md focus:ring-1 focus:ring-primary focus:border-primary outline-none w-48"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-tf-border-sub bg-tf-bg-muted/50">
              <TH field="ad_name" label="Ad Name" align="left" />
              <th className="px-3 py-2.5 text-[11px] font-medium text-tf-text-muted uppercase tracking-wider text-left whitespace-nowrap">
                Ad Status
              </th>
              <th className="px-3 py-2.5 text-[11px] font-medium text-tf-text-muted uppercase tracking-wider text-left whitespace-nowrap">
                Type
              </th>
              <TH field="days_running" label="Days" />
              <TH field="roas" label="ROAS" />
              <TH field="spend" label="Spend" />
              <TH field="revenue" label="Revenue" />
              <TH field="ctr" label="CTR" />
              <TH field="cpm" label="CPM" />
            </tr>
          </thead>
          <tbody>
            {page.map((c) => {
              const badge = getStatusBadge(c.performance_status)
              return (
                <tr
                  key={c.ad_id}
                  className="border-b border-tf-border-sub hover:bg-tf-feedback-info-bg/30 transition-colors cursor-pointer group"
                  onClick={() => onCreativeClick?.(c)}
                >
                  {/* Ad Name + Thumbnail */}
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-tf-bg-hover overflow-hidden flex-shrink-0">
                        {c.thumbnail_url ? (
                          <img
                            src={c.thumbnail_url}
                            alt=""
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-tf-text-muted">
                            <Icons.Image className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-tf-text truncate max-w-[260px] group-hover:text-primary transition-colors">
                          {c.ad_name || 'Unnamed'}
                        </p>
                        <p className="text-[11px] text-tf-text-muted truncate max-w-[260px]">
                          Campaign
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-3 py-2.5">
                    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium', badge.bg, badge.text)}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                      {badge.label}
                    </span>
                  </td>

                  {/* Type */}
                  <td className="px-3 py-2.5 text-[12px] text-tf-text-muted whitespace-nowrap">
                    {c.creative_type || 'IMAGE'}
                  </td>

                  {/* Days */}
                  <td className="px-3 py-2.5 text-[12px] text-tf-text-muted text-right tabular-nums">
                    {c.days_running || 0}
                  </td>

                  {/* ROAS */}
                  <td className="px-3 py-2.5 text-right">
                    <span className={cn(
                      'text-[12px] font-semibold tabular-nums',
                      (c.metrics?.roas || 0) >= 1.5 ? 'text-tf-feedback-success-text' :
                      (c.metrics?.roas || 0) >= 1.0 ? 'text-tf-text' :
                      'text-tf-feedback-error-text'
                    )}>
                      {(c.metrics?.roas || 0).toFixed(2)}
                    </span>
                  </td>

                  {/* Spend */}
                  <td className="px-3 py-2.5 text-[12px] text-tf-text-sub text-right tabular-nums font-medium">
                    {fmtCurrency(c.metrics?.spend || 0)}
                  </td>

                  {/* Revenue */}
                  <td className="px-3 py-2.5 text-[12px] text-tf-text-muted text-right tabular-nums">
                    {fmtCurrency(c.metrics?.revenue || 0)}
                  </td>

                  {/* CTR */}
                  <td className="px-3 py-2.5 text-[12px] text-tf-text-muted text-right tabular-nums">
                    {fmtCompact(c.metrics?.ctr || 0)}
                  </td>

                  {/* CPM */}
                  <td className="px-3 py-2.5 text-[12px] text-tf-text-muted text-right tabular-nums">
                    {fmtCurrency(c.metrics?.cpm || 0)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Empty filter results */}
      {filtered.length === 0 && (searchQuery || statusFilter) && (
        <div className="p-10 text-center">
          <Icons.Search className="w-6 h-6 text-tf-text-muted mx-auto mb-2" />
          <p className="text-sm text-tf-text-muted">No creatives match your filters</p>
        </div>
      )}

      {/* Pagination - Antares style */}
      {filtered.length > 0 && (
        <div className="px-5 py-3 border-t border-tf-border-sub bg-tf-bg-card">
          <div className="flex items-center justify-between">
            <p className="text-xs text-tf-text-muted">
              Showing {startIdx + 1} to {Math.min(startIdx + ITEMS_PER_PAGE, filtered.length)} of{' '}
              {filtered.length.toLocaleString()} creatives
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={cn(
                  'w-7 h-7 flex items-center justify-center rounded text-xs transition-colors',
                  currentPage === 1 ? 'text-tf-text-muted cursor-not-allowed' : 'text-tf-text-muted hover:bg-tf-bg-hover',
                )}
              >
                <Icons.ChevronLeft className="w-3.5 h-3.5" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let num: number
                if (totalPages <= 5) num = i + 1
                else if (currentPage <= 3) num = i + 1
                else if (currentPage >= totalPages - 2) num = totalPages - 4 + i
                else num = currentPage - 2 + i
                return (
                  <button
                    key={num}
                    onClick={() => setCurrentPage(num)}
                    className={cn(
                      'w-7 h-7 flex items-center justify-center rounded text-xs font-medium transition-colors',
                      currentPage === num
                        ? 'bg-tf-bg-page text-white'
                        : 'text-tf-text-muted hover:bg-tf-bg-hover',
                    )}
                  >
                    {num}
                  </button>
                )
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={cn(
                  'w-7 h-7 flex items-center justify-center rounded text-xs transition-colors',
                  currentPage === totalPages ? 'text-tf-text-muted cursor-not-allowed' : 'text-tf-text-muted hover:bg-tf-bg-hover',
                )}
              >
                <Icons.ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreativeTable
